import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from "react";
import { useHandleError } from "../hooks/useHandleError";
import * as quizService from "../services/moduleQuizService";

export interface QuizSession {
  sessionId: string;
  moduleId: string;
  startTime: number;
  currentQuestionIndex: number;
  answers: Map<string, string | number>;
  isCompleted: boolean;
  score?: number;
}

export interface ModuleProgress {
  moduleId: string;
  moduleName: string;
  weekNumber: number;
  completionPercentage: number;
  sectionsCompleted: string[];
  quizCompleted: boolean;
  quizScore?: number;
  lastAccessed: number;
}

export interface StudentAnalytics {
  studentId: string;
  totalStudyTime: number;
  averageQuizScore: number;
  moduleProgresses: ModuleProgress[];
  strengthAreas: string[];
  improvementAreas: string[];
  lastUpdated: number;
}

export type QuizStatus = "not-started" | "in-progress" | "completed";

export interface StudyContentSession {
  sessionId: string;
  title: string;
  type: "quiz" | "flashcards" | "practice";
  weekNumber: number;
  startTime: number;
  currentIndex: number;
  answers: Map<string, any>;
  isCompleted: boolean;
  score?: number;
  results?: any;
}

export interface StudyContentProgress {
  sessionId: string;
  sessionType: "quiz" | "flashcards" | "practice";
  weekNumber: number;
  completionPercentage: number;
  bestScore?: number;
  attempts: number;
  lastAttempted: number;
  status: "not-started" | "in-progress" | "completed";
}

export type StudyContentStatus = "not-started" | "in-progress" | "completed";

interface StudentClassroomContextType {
  // Current learning session state
  currentProgram: string;
  setCurrentProgram: (program: string) => void;

  currentWeek: number;
  setCurrentWeek: (week: number) => void;

  currentModule: string;
  setCurrentModule: (module: string) => void;

  // Quiz state
  quizStatus: QuizStatus;
  setQuizStatus: (status: QuizStatus) => void;

  currentQuizSession: QuizSession | null;
  setCurrentQuizSession: (session: QuizSession | null) => void;

  // Progress tracking
  moduleProgresses: { [key: string]: ModuleProgress };
  setModuleProgresses: (progresses: { [key: string]: ModuleProgress }) => void;

  // Analytics (cached from API)
  studyAnalytics: StudentAnalytics | null;
  setStudyAnalytics: (analytics: StudentAnalytics | null) => void;

  // Questions state
  currentModuleQuestions: any[];
  setCurrentModuleQuestions: (questions: any[]) => void;

  // Study Content state
  currentStudyContentSession: StudyContentSession | null;
  setCurrentStudyContentSession: (session: StudyContentSession | null) => void;

  studyContentProgresses: { [key: string]: StudyContentProgress };
  setStudyContentProgresses: (progresses: {
    [key: string]: StudyContentProgress;
  }) => void;

  // Context methods
  startQuizSession: (moduleId: string) => Promise<QuizSession>;
  endQuizSession: (
    moduleId: string,
    session: QuizSession,
    finalAnswers: Map<string, string | number>
  ) => Promise<void>;
  updateModuleProgress: (
    moduleId: string,
    progress: Partial<ModuleProgress>
  ) => void;
  fetchStudentAnalytics: () => Promise<void>;
  fetchModuleQuestions: (moduleId: string) => Promise<void>;

  // Study Content methods
  startStudyContentSession: (
    sessionId: string,
    sessionType: "quiz" | "flashcards" | "practice",
    weekNumber: number
  ) => StudyContentSession;
  endStudyContentSession: (session: StudyContentSession, results: any) => void;
  updateStudyContentProgress: (
    sessionId: string,
    progress: Partial<StudyContentProgress>
  ) => void;

  // Loading states
  isLoadingQuestions: boolean;
  isLoadingAnalytics: boolean;
}

const StudentClassroomContext = createContext<
  StudentClassroomContextType | undefined
>(undefined);

export const useStudentClassroom = () => {
  const context = useContext(StudentClassroomContext);
  if (!context) {
    throw new Error(
      "useStudentClassroom must be used within a StudentClassroomProvider"
    );
  }
  return context;
};

interface StudentClassroomProviderProps {
  children: ReactNode;
}

export const StudentClassroomProvider: React.FC<
  StudentClassroomProviderProps
> = ({ children }) => {
  const handleError = useHandleError();

  // Core state
  const [currentProgram, setCurrentProgram] = useState<string>("");
  const [currentWeek, setCurrentWeek] = useState<number>(1);
  const [currentModule, setCurrentModule] = useState<string>("");

  // Quiz state
  const [quizStatus, setQuizStatus] = useState<QuizStatus>("not-started");
  const [currentQuizSession, setCurrentQuizSession] =
    useState<QuizSession | null>(null);

  // Progress and analytics
  const [moduleProgresses, setModuleProgresses] = useState<{
    [key: string]: ModuleProgress;
  }>({});

  // const [moduleProgresses, setModuleProgresses] = useState<{
  //   [key: string]: ModuleProgress;
  // }>({
  //   // Initialize with mock progress for testing
  //   eap_yellow_week1_module7: {
  //     moduleId: "eap_yellow_week1_module7",
  //     moduleName: "Reading Techniques",
  //     weekNumber: 1,
  //     completionPercentage: 100, // All sections completed
  //     sectionsCompleted: ["skimming", "scanning", "vocabulary", "main-ideas"],
  //     quizCompleted: false, // Quiz not completed yet so we can test it
  //     lastAccessed: Date.now(),
  //   },
  // });

  const [studyAnalytics, setStudyAnalytics] = useState<StudentAnalytics | null>(
    null
  );

  // Questions state
  const [currentModuleQuestions, setCurrentModuleQuestions] = useState<any[]>(
    []
  );

  // Study Content state
  const [currentStudyContentSession, setCurrentStudyContentSession] =
    useState<StudyContentSession | null>(null);

  const [studyContentProgresses, setStudyContentProgresses] = useState<{
    [key: string]: StudyContentProgress;
  }>({});

  // Loading states
  const [isLoadingQuestions, setIsLoadingQuestions] = useState<boolean>(false);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState<boolean>(false);

  // Start a new quiz session
  const startQuizSession = useCallback(
    async (moduleId: string): Promise<QuizSession> => {
      try {
        const sessionId = `quiz_${moduleId}_${Date.now()}`;
        const newSession: QuizSession = {
          sessionId,
          moduleId,
          startTime: Date.now(),
          currentQuestionIndex: 0,
          answers: new Map(),
          isCompleted: false,
        };

        setCurrentQuizSession(newSession);
        setQuizStatus("in-progress");

        // Log quiz start to backend
        await quizService.startQuizSession(sessionId, moduleId);

        return newSession;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    [handleError]
  );

  // End quiz session and submit results
  const endQuizSession = useCallback(
    async (
      moduleId: string,
      session: QuizSession,
      finalAnswers: Map<string, string | number>
    ): Promise<void> => {
      try {
        const endTime = Date.now();
        const timeSpent = endTime - session.startTime;

        // Submit quiz results to backend
        const result = await quizService.submitQuizAnswers({
          sessionId: session.sessionId,
          moduleId: moduleId,
          answers: Object.fromEntries(finalAnswers),
          timeSpent,
          completedAt: endTime,
        });

        // Update local state
        const updatedSession: QuizSession = {
          ...session,
          moduleId: moduleId,
          answers: finalAnswers,
          isCompleted: true,
          score: result.data.score,
        };

        setCurrentQuizSession(updatedSession);
        setQuizStatus("completed");

        // Update module progress
        updateModuleProgress(moduleId, {
          quizCompleted: true,
          quizScore: result.data.score,
          completionPercentage: 100,
        });
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    [handleError]
  );

  // Update module progress
  const updateModuleProgress = useCallback(
    (moduleId: string, progress: Partial<ModuleProgress>) => {
      setModuleProgresses((prev) => ({
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          ...progress,
          lastAccessed: Date.now(),
        } as ModuleProgress,
      }));
    },
    []
  );

  // Fetch student analytics from backend
  const fetchStudentAnalytics = useCallback(async (): Promise<void> => {
    try {
      setIsLoadingAnalytics(true);
      const response = await quizService.getStudentAnalytics();
      setStudyAnalytics(response.data);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, [handleError]);

  // Fetch module questions
  const fetchModuleQuestions = useCallback(
    async (moduleId: string): Promise<void> => {
      console.log("🔍 Fetching questions for moduleId:", moduleId);
      try {
        setIsLoadingQuestions(true);
        const response = await quizService.getModuleQuestions(moduleId);
        console.log("📚 Questions response:", response);

        // ✅ FIXED - response already has questions directly
        const questions = response.questions || [];
        console.log("📝 Setting questions:", questions);

        setCurrentModuleQuestions(questions);
      } catch (error) {
        console.error("❌ Failed to fetch questions:", error);
        handleError(error);
        setCurrentModuleQuestions([]);
      } finally {
        setIsLoadingQuestions(false);
      }
    },
    [handleError]
  );

  // Start a study content session
  const startStudyContentSession = useCallback(
    (
      sessionId: string,
      sessionType: "quiz" | "flashcards" | "practice",
      weekNumber: number
    ): StudyContentSession => {
      const newSession: StudyContentSession = {
        sessionId,
        title: getSessionTitle(sessionId, sessionType),
        type: sessionType,
        weekNumber,
        startTime: Date.now(),
        currentIndex: 0,
        answers: new Map(),
        isCompleted: false,
      };

      setCurrentStudyContentSession(newSession);

      // Initialize progress if not exists
      if (!studyContentProgresses[sessionId]) {
        updateStudyContentProgress(sessionId, {
          sessionId,
          sessionType,
          weekNumber,
          completionPercentage: 0,
          attempts: 0,
          lastAttempted: Date.now(),
          status: "in-progress",
        });
      } else {
        updateStudyContentProgress(sessionId, {
          lastAttempted: Date.now(),
          status: "in-progress",
        });
      }

      return newSession;
    },
    [studyContentProgresses]
  );

  // End study content session
  const endStudyContentSession = useCallback(
    (session: StudyContentSession, results: any): void => {
      const updatedSession: StudyContentSession = {
        ...session,
        isCompleted: true,
        score: results.score || results.accuracy || 0,
        results,
      };

      setCurrentStudyContentSession(updatedSession);

      // Update progress
      const currentProgress = studyContentProgresses[session.sessionId];
      const newAttempts = (currentProgress?.attempts || 0) + 1;
      const newBestScore = Math.max(
        currentProgress?.bestScore || 0,
        results.score || results.accuracy || 0
      );

      updateStudyContentProgress(session.sessionId, {
        completionPercentage: 100,
        bestScore: newBestScore,
        attempts: newAttempts,
        status: "completed",
        lastAttempted: Date.now(),
      });
    },
    [studyContentProgresses]
  );

  // Update study content progress
  const updateStudyContentProgress = useCallback(
    (sessionId: string, progress: Partial<StudyContentProgress>): void => {
      setStudyContentProgresses((prev) => ({
        ...prev,
        [sessionId]: {
          ...prev[sessionId],
          ...progress,
          lastAttempted: Date.now(),
        } as StudyContentProgress,
      }));
    },
    []
  );

  // Helper function to get session titles
  const getSessionTitle = (sessionId: string, sessionType: string): string => {
    if (sessionId.includes("vocab")) return "Vocabulary Quiz";
    if (sessionId.includes("grammar")) return "Grammar Flashcards";
    if (sessionId.includes("listening")) return "Listening Comprehension";
    if (sessionId.includes("reading")) return "Reading Comprehension Quiz";

    switch (sessionType) {
      case "quiz":
        return "Study Quiz";
      case "flashcards":
        return "Study Flashcards";
      case "practice":
        return "Practice Session";
      default:
        return "Study Session";
    }
  };

  const contextValue: StudentClassroomContextType = {
    // Current learning session state
    currentProgram,
    setCurrentProgram,
    currentWeek,
    setCurrentWeek,
    currentModule,
    setCurrentModule,

    // Quiz state
    quizStatus,
    setQuizStatus,
    currentQuizSession,
    setCurrentQuizSession,

    // Progress tracking
    moduleProgresses,
    setModuleProgresses,

    // Analytics
    studyAnalytics,
    setStudyAnalytics,

    // Questions
    currentModuleQuestions,
    setCurrentModuleQuestions,

    // Study Content state
    currentStudyContentSession,
    setCurrentStudyContentSession,
    studyContentProgresses,
    setStudyContentProgresses,

    // Context methods
    startQuizSession,
    endQuizSession,
    updateModuleProgress,
    fetchStudentAnalytics,
    fetchModuleQuestions,

    // Study Content methods
    startStudyContentSession,
    endStudyContentSession,
    updateStudyContentProgress,

    // Loading states
    isLoadingQuestions,
    isLoadingAnalytics,
  };

  return (
    <StudentClassroomContext.Provider value={contextValue}>
      {children}
    </StudentClassroomContext.Provider>
  );
};
